import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { formatApiErrorDetail } from "../lib/api";
import AuthShell from "../components/AuthShell";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";

function formatTelefone(val) {
  const digits = (val || "").replace(/\D/g, "").slice(0, 11);
  if (digits.length === 0) return "";
  if (digits.length <= 2) return `(${digits}`;
  if (digits.length <= 7) return `(${digits.slice(0, 2)}) ${digits.slice(2)}`;
  return `(${digits.slice(0, 2)}) ${digits.slice(2, 7)}-${digits.slice(7)}`;
}

export default function Cadastro() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [telefone, setTelefone] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    const cleanPhone = telefone.replace(/\D/g, "");
    if (!cleanPhone) {
      setError("Informe seu WhatsApp com DDD.");
      return;
    }
    // Validação celular brasileiro: DDD (11 a 99) + 9 dígitos iniciando em 9
    const celularRegex = /^[1-9]{2}9\d{8}$/;
    if (!celularRegex.test(cleanPhone)) {
      setError("WhatsApp inválido. Digite o DDD e seu celular com 9 dígitos (ex.: (44) 99999-8888).");
      return;
    }

    if (password !== confirm) {
      setError("As senhas não coincidem");
      return;
    }

    setLoading(true);
    try {
      await register(name, email, password, cleanPhone);
      toast.success("Conta criada! Agora escolha seu plano para liberar os chips.");
      navigate("/planos", { state: { locked: true } });
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell
      title="Criar sua conta"
      subtitle="Crie sua conta no MaturaMax e blinde seus chips contra banimentos agora mesmo."
      footer={
        <span>
          Já tem conta?{" "}
          <Link to="/login" className="text-emerald-400 hover:text-emerald-300 font-medium" data-testid="link-login">
            Fazer login
          </Link>
        </span>
      }
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        {error && (
          <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3 text-sm text-red-300" data-testid="auth-register-error">
            {error}
          </div>
        )}
        <div className="space-y-1.5">
          <Label htmlFor="name" className="text-slate-300">Nome completo</Label>
          <Input id="name" required value={name} onChange={(e) => setName(e.target.value)} placeholder="Seu nome"
            data-testid="auth-register-name-input"
            className="bg-slate-800/60 border-slate-700 text-slate-100 placeholder:text-slate-500 focus-visible:ring-2 focus-visible:ring-emerald-500" />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="email" className="text-slate-300">E-mail</Label>
          <Input id="email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="voce@empresa.com.br"
            data-testid="auth-register-email-input"
            className="bg-slate-800/60 border-slate-700 text-slate-100 placeholder:text-slate-500 focus-visible:ring-2 focus-visible:ring-emerald-500" />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="telefone" className="text-slate-300">WhatsApp (com DDD)</Label>
          <Input id="telefone" type="tel" required value={telefone}
            onChange={(e) => setTelefone(formatTelefone(e.target.value))}
            placeholder="(44) 99999-8888"
            maxLength={15}
            data-testid="auth-register-phone-input"
            className="bg-slate-800/60 border-slate-700 text-slate-100 placeholder:text-slate-500 focus-visible:ring-2 focus-visible:ring-emerald-500 font-mono" />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="password" className="text-slate-300">Senha</Label>
          <Input id="password" type="password" required minLength={6} value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Mínimo 6 caracteres"
            data-testid="auth-register-password-input"
            className="bg-slate-800/60 border-slate-700 text-slate-100 placeholder:text-slate-500 focus-visible:ring-2 focus-visible:ring-emerald-500" />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="confirm" className="text-slate-300">Confirmar senha</Label>
          <Input id="confirm" type="password" required value={confirm} onChange={(e) => setConfirm(e.target.value)} placeholder="Repita a senha"
            data-testid="auth-register-confirm-input"
            className="bg-slate-800/60 border-slate-700 text-slate-100 placeholder:text-slate-500 focus-visible:ring-2 focus-visible:ring-emerald-500" />
        </div>
        <Button type="submit" disabled={loading} data-testid="auth-register-submit-button"
          className="w-full bg-emerald-500 hover:bg-emerald-600 text-emerald-950 font-semibold h-11 transition-colors duration-150">
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Criar conta"}
        </Button>
      </form>
    </AuthShell>
  );
}
